import * as React from "react";
import { EMDASH } from "@eave/shared/utils/general";

import EditableDateValue from "./EditableDateValue";
import EditableSelectValue from "./EditableSelectValue";
import EditableTextValue from "./EditableTextValue";
import EditorPage from "./EditorPage";
import ReadOnlyValue from "./ReadOnlyValue";
import WorksheetAsLink from "./WorksheetAsLink";
import WorksheetSliceAsTable from "./WorksheetSliceAsTable";
import { FIELD_TYPE, getFieldDef } from "./wsDefs";
import { IWorksheetState } from "./interfaces";

const FontAwesome = require("react-fontawesome");

const styles = require("./styles.module.scss");

const WrapWithHint: React.SFC<{ children: any }> = ({ children }) => (
  <div className={styles.hintWrapper}>
    {children}
    <span className={styles.hint}> ← double-click to edit </span>
  </div>
);

const Value: React.SFC<{
  worksheets: IWorksheetState[];
  ws: IWorksheetState;
  field: string;
  onUpdate: Function;
  setCurrentWsId: Function;
  addWsToParent: Function;
  deleteWsFromParent: Function;
  showChildrenInline?: boolean;
  showHidden?: boolean;
  readOnly?: boolean;
}> = ({
  worksheets,
  ws,
  field,
  onUpdate,
  setCurrentWsId,
  addWsToParent,
  deleteWsFromParent,
  showChildrenInline,
  showHidden,
  readOnly,
}) => {
  if (!ws.instance.hasOwnProperty(field)) {
    throw new Error(`field ${field} does not exist on worksheet ${ws.name}`);
  }

  const fieldDef = getFieldDef(ws.name, field);
  const value = fieldDef.transformValue
    ? fieldDef.transformValue(ws.instance[field])
    : ws.instance[field];

  const sharedProps = {
    value,
    onUpdate: val =>
      onUpdate(ws.id, field, fieldDef.beforeUpdate ? fieldDef.beforeUpdate(val) : val),
  };

  switch (fieldDef.type) {
    case FIELD_TYPE.BOOL: {
      if (fieldDef.readOnly || readOnly) {
        return <ReadOnlyValue value={value} formatting={fieldDef.formatting} />;
      }
      const opts = [
        { value: null, label: EMDASH },
        { value: false, label: "No" },
        { value: true, label: "Yes" },
      ];
      return (
        <WrapWithHint>
          <EditableSelectValue
            {...sharedProps}
            selectOpts={opts}
            searchable={false}
            formatting={fieldDef.formatting}
          />
        </WrapWithHint>
      );
    }
    case FIELD_TYPE.DATE: {
      if (fieldDef.readOnly || readOnly) {
        return <ReadOnlyValue value={value} formatting={fieldDef.formatting} />;
      }
      return <EditableDateValue {...sharedProps} field={`${ws.id}-${field}`} />;
    }
    case FIELD_TYPE.NUMBER: {
      if (fieldDef.readOnly || readOnly) {
        return <ReadOnlyValue value={value} formatting={fieldDef.formatting} />;
      }
      return (
        <WrapWithHint>
          <EditableTextValue
            {...sharedProps}
            precision={fieldDef.precision}
            disallowNegative={fieldDef.disallowNegative}
            formatting={fieldDef.formatting}
          />
        </WrapWithHint>
      );
    }
    case FIELD_TYPE.TEXT: {
      if (fieldDef.readOnly || readOnly) {
        return <ReadOnlyValue value={value} formatting={fieldDef.formatting} />;
      }
      if (fieldDef.enumValues) {
        const opts = [
          { value: null, label: EMDASH },
          ...fieldDef.enumValues.map(v => ({ value: v, label: v })),
        ];
        return (
          <WrapWithHint>
            <EditableSelectValue
              {...sharedProps}
              selectOpts={opts}
              formatting={fieldDef.formatting}
              searchable
            />
          </WrapWithHint>
        );
      }
      return (
        <WrapWithHint>
          <EditableTextValue
            {...sharedProps}
            precision={fieldDef.precision}
            disallowNegative={fieldDef.disallowNegative}
            formatting={fieldDef.formatting}
          />
        </WrapWithHint>
      );
    }
    case FIELD_TYPE.WS: {
      if (showChildrenInline && value !== null) {
        return (
          <div className={styles.pageChildWorksheet}>
            <EditorPage
              worksheets={worksheets}
              wsId={value}
              fieldUpdate={onUpdate}
              setCurrentWsId={setCurrentWsId}
              addWsToParent={addWsToParent}
              deleteWsFromParent={deleteWsFromParent}
              showChildrenInline={showChildrenInline}
              showHidden={showHidden}
              readOnly={fieldDef.readOnly || readOnly}
            />
          </div>
        );
      }
      return (
        <WorksheetAsLink
          id={value}
          onClickLink={setCurrentWsId}
          readOnly={readOnly}
          onClickAdd={() => {
            const confirmed = confirm("Are you sure?");
            if (confirmed) {
              addWsToParent(fieldDef.wsName, ws.id, field);
            }
          }}
        />
      );
    }
    case FIELD_TYPE.WS_SLICE: {
      return (
        <WorksheetSliceAsTable
          worksheets={worksheets}
          wsName={fieldDef.wsName}
          wsIds={value}
          onClickLink={setCurrentWsId}
          onClickAdd={() => addWsToParent(fieldDef.wsName, ws.id, field)}
          onClickDelete={wsId => deleteWsFromParent(wsId, ws.id, field)}
          onUpdate={onUpdate}
          setCurrentWsId={setCurrentWsId}
          addWsToParent={addWsToParent}
          deleteWsFromParent={deleteWsFromParent}
          showHidden={showHidden}
          readOnly={fieldDef.readOnly || readOnly}
        />
      );
    }
    default: {
      throw new Error(`editor for field type ${fieldDef.type} not yet implemented`);
    }
  }
};

export default Value;
